<template>
  <article class="preferred-funds-wrapper">
    <section class="preferred-funds-container">
      <header-simple :title="title" />
      <stepper :steps="steps" :current="currentStep" />
      <hr class="divider">
      <div class="preferred-funds-step__container">
        <div class="preferred-funds-step step-0" v-if="currentStep == 0">
          <div class="preferred-funds-result">
            <div class="preferred-funds-result__header">
              <h3 class="preferred-funds-result__title icon-file-excel">
                Step 1: Prepare your file
              </h3>
            </div>
            <div class="preferred-funds-result__content">
              <p>
                To import your file, format your Excel file (xlsx) using the following names for
                your columns and formats for your cells:
              </p>
              <p class="preferred-funds-file-type-header">General Approval File Example</p>
              <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body">
                <template v-slot:caption>{{ '' }}</template>
              </import-format-table>
            </div>
            <div class="preferred-funds-result__header">
              <h3 class="preferred-funds-result__title icon-file-add">
                Step 2: Upload your formatted file
              </h3>
            </div>
            <div class="preferred-funds-result__content">
              <div class="preferred-funds__file-container">
                <form action="" enctype="multipart/form-data" class="mds-form">
                  <div class="mds-form__field-group">
                    <input
                      class="mds-form__input preferred-funds__file-input"
                      type="file"
                      aria-label="Choose file"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      @change="onSelectFile"
                      ref="file"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="actions-bar">
            <div class="box left">
              <button class="mds-button mds-button--secondary" type="button" @click="cancelImport">
                Cancel
              </button>
            </div>
            <div class="box right">
              <mds-button class= "preferred-button__start-file-validation"
              variation="primary"
              :disabled="!canContinue"
              @click="startValidation"
              >
                Continue
              </mds-button>
            </div>
          </div>
        </div>
        <div class="preferred-funds-step step-1" v-if="currentStep == 1">
          <template v-if="!this.error">
            <mds-loader />
            <h3 class="preferred-funds-step__title centered">Checking {{ fileName }}</h3>
          </template>
          <template v-else>
            <h3 v-if="hasValidationFailed" class="preferred-funds-step__title">
              We found a few problems and were unable to import your preferred funds.<br />
              Please review issues below and update your spreadsheet.
            </h3>
            <h3 v-else class="preferred-funds-step__title">
              Our check is complete. Please review the items below.
            </h3>

            <div class="preferred-funds-result__container">
              <div class="preferred-funds-result" v-if="errorMessage">
                <div class="preferred-funds-result__header">
                  <h3 class="preferred-funds-result__title preferred-funds-result__title icon-not-ready">
                    There has occurred an error in the importing preferred funds process
                  </h3>
                </div>
                <div class="preferred-funds-result__content preferred-funds-result__error">
                  <p>
                    {{errorMessage}}
                  </p>
                </div>
              </div>

              <div class="preferred-funds-result" v-if="isInvalidExtensionCheck">
                <div class="preferred-funds-result__header">
                  <h3 class="preferred-funds-result__title icon-not-ready">
                    Your spreadsheet has an extension issue. Only *.xlsx files allowed.
                  </h3>
                </div>
                <div class="preferred-funds-result__content">
                  <p>
                    Check the format of your Excel file with the proper extension (xlsx) to make sure
                    you are importing a valid file:
                  </p>
                  <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body">
                    <template v-slot:caption>{{ '' }}</template>
                  </import-format-table>
                </div>
              </div>

              <div class="actions-bar">
                <div class="box left">
                  <mds-button variation="flat" icon="caret-left" type="button" @click="startAgain">
                    Back
                  </mds-button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="preferred-funds-step step-2" v-if="currentStep == 2">
          <h3 v-if="hasValidationFailed" class="preferred-funds-step__title">
            We found a few problems and were unable to import your preferred funds.<br />
            Please review issues below and update your spreadsheet.
          </h3>
          <h3 v-else class="preferred-funds-step__title">
            Our check is complete. Please review the items below.
          </h3>

          <div class="preferred-funds-result__container">
            <!-- INVALID FORMAT ERROR -->
            <div
              class="preferred-funds-result"
              v-if="showInvalidFormatError"
            >
              <div class="preferred-funds-result__header">
                <h3 class="preferred-funds-result__title icon-not-ready">
                  Your spreadsheet has formatting issues
                </h3>
              </div>
              <div class="preferred-funds-result__content preferred-funds-result__error">
                <p>
                  Check the format of your Excel file (xlsx) to make sure you are using the following
                  names for your columns and formats for your cells:
                </p>
                <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body" >
                  <template v-slot:caption>
                    {{ '' }}
                  </template>
                </import-format-table>
              </div>
            </div>

            <!-- ERROR -->
            <div
              class="preferred-funds-result"
              v-if="hasValidationFailedOrPartialSuccess"
            >
               <div class="preferred-funds-result__header">
                <h3 class="preferred-funds-result__title preferred-funds-result__title icon-not-ready">
                  Preferred funds not ready for import
                </h3>
              </div>
              <div class="preferred-funds-result__content" v-if="validationOutput.invalid">
                <import-error-table :errorData="validationOutput.invalid"></import-error-table>
              </div>
            </div>

            <!-- SUCCESS -->
            <div class="preferred-funds-result" v-if="!hasValidationFailed">
              <div class="preferred-funds-result__header">
                <h3 class="preferred-funds-result__title preferred-funds-result__title icon-ready">
                  Preferred funds ready for import
                </h3>
              </div>
              <div class="preferred-funds-result__content preferred-funds-result__success">
                <!-- eslint-disable-next-line max-len -->
                <p> Click continue to import {{ validationOutput.successfullyImportedPreferredFundCount }} preferred funds </p>
              </div>
            </div>

            <!-- ACTION BAR -->
            <div class="actions-bar">
              <div class="box left">
                <mds-button variation="flat" icon="caret-left" type="button" @click="startAgain">
                  Back
                </mds-button>
              </div>
              <div class="box right">
                <mds-button v-if="hasValidationFailed"
                  variation="primary"
                  @click="cancelImport"
                >
                  Return to Universe Funds
                </mds-button>
                <template v-else>
                  <button class="mds-button mds-button--secondary review-button__cancel-import"
                    type="button"
                    @click="cancelImport"
                  >
                    Cancel
                   </button>
                    <mds-button class="review-button__start-import"
                    variation="primary"
                    @click="startImport"
                  >
                    Continue
                  </mds-button>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="preferred-funds-step" v-if="currentStep == 3">
          <h3 class="preferred-funds-step__title centered">Importing {{ fileName }}</h3>
          <progress-bar :duration="totalValidateTime" :enabled="currentStep == 3" :complete="importCompleted" />
        </div>
      </div>
    </section>
 </article>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import {
  concat,
  get,
  includes,
  filter,
  map,
  reduce,
} from 'lodash';
import { goToLogoutRoute } from '@/utils';
import Stepper from '@/components/atoms/Stepper';
import { columnsFormat, steps } from '@/config/import-preferred-funds-format.json';
import HeaderSimple from '@/components/atoms/HeaderSimple';
import ImportFormatTable from '@/components/atoms/ImportFormatTable';
import ImportErrorTable from '@/components/organisms/ImportErrorTable';
import ProgressBar from '@/components/atoms/ProgressBar';

import * as ImportPreferredFundsService from '@/services/ImportPreferredFunds';

export default {
  name: 'import-preferred-funds',
  components: {
    Stepper,
    HeaderSimple,
    ImportFormatTable,
    ImportErrorTable,
    ProgressBar,
  },
  data() {
    return {
      title: 'Import Funds to mark them as preferred at provider product level',
      columnsFormat,
      steps,
      canContinue: false,
      currentStep: 0,
      error: false,
      filePath: '',
      fileName: '',
      validationStatus: '',
      invalidStatuses: [],
      requestUrl: null,
      errorMessage: '',
      validationOutput: {},
      totalValidateTime: 0,
      importCompleted: false,
    };
  },
  computed: {
    ...mapState({
      selectedClient: (state) => state.grid.client.selected,
    }),
    isInvalidExtensionCheck() {
      return (
        this.validationStatus === 'ValidationFailed'
        && this.checkInvalidStatus(['InvalidExtension'])
      );
    },
    hasValidationFailed() {
      return this.validationStatus === 'ValidationFailed';
    },
    showInvalidFormatError() {
      return this.hasValidationFailed && this.checkInvalidStatus(['InvalidFormat']);
    },
    hasValidationFailedOrPartialSuccess() {
      // eslint-disable-next-line max-len
      return (this.hasValidationFailed && !this.checkInvalidStatus(['InvalidFormat'])) || (this.validationOutput.invalid && this.validationStatus !== 'ValidationFailed');
    },
  },
  methods: {
    ...mapActions(['openNotification']),
    cancelImport() {
      this.$router.back();
    },
    onSelectFile(event) {
      this.canContinue = true;
      this.filePath = '';
      if (!this.validateFile(event.target)) {
        this.canContinue = false;
        this.error = true;
        this.filePath = event.target.value;
        this.currentStep = 1;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses.push('InvalidExtension');
      }
    },
    validateFile(fileObj) {
      const isNotEmpty = fileObj.value !== '';
      const isOnlyOneFile = fileObj.files && fileObj.files.length === 1;
      const extension = fileObj.value.split('.').pop();
      const hasAllowedExtensionFile = ['xlsx'].indexOf(extension) >= 0;
      return isNotEmpty && isOnlyOneFile && hasAllowedExtensionFile;
    },
    async startValidation() {
      const file = get(this.$refs, 'file.files[0]', {});
      this.fileName = file.name;
      this.currentStep = 1;
      this.startValidateTime = new Date().getTime();

      this.error = false;
      this.errorMessage = '';
      ImportPreferredFundsService.validatePreferredFundsFile({
        file,
        clientId: this.selectedClient,
      }).then((response) => {
        this.error = false;
        this.errorMessage = '';
        this.validationOutput = get(response, 'data', {});
        this.validationStatus = get(this.validationOutput, 'status', 'ValidationFailed');

        if (!response.status.success) {
          // General Error and the error message will be inside the response.status object
          this.error = true;
          this.errorMessage = response.status.message;
          this.invalidStatuses = ['InvalidData'];
          this.validationOutput = {
            ...this.validationOutput,
            invalid: map(this.validationOutput.invalid, (invalidData, index) => ({
              ...invalidData,
              secId: invalidData.secId || `invalid-${index}`,
            })),
          };
        } else {
          // There could be one or more errors but more related to a specific row of the file
          this.invalidStatuses = [
            ...new Set(
              reduce(
                this.validationOutput.invalid,
                (list, item) => concat(list, item.errors),
                [],
              ),
            ),
          ];
        }
        this.currentStep = 2;
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
      }).catch((error) => {
        this.error = true;
        this.errorMessage = error.message;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses = ['InvalidData'];
        this.handleError(error);
        this.openNotification({
          title: 'Validation Preferred funds Error',
          message: 'Validation of Import Preferred funds process has pesented an error.',
          notificationType: 'error',
          showContactInfo: true,
        });
      });
    },
    checkInvalidStatus(value) {
      let invalid = false;
      map(value, (v) => {
        if (includes(this.invalidStatuses, v)) {
          invalid = true;
        }
      });
      return invalid;
    },
    startAgain() {
      this.filePath = '';
      this.currentStep = 0;
      this.invalidStatuses = [];
      this.error = false;
      this.validationStatus = '';
      this.validationOutput = {};
      this.canContinue = false;
      this.importCompleted = false;
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else if (httpStatus === 400) {
        // Custom error messages for failed validation
        this.errorMessage = error.response.data?.status?.message;
      }
    },
    startImport() {
      this.currentStep = 3;
      this.startValidateTime = new Date().getTime();
      const payload = filter(this.validationOutput.valid, (item) => item.fiduciaryFundID);
      ImportPreferredFundsService.importPreferredFunds({
        payload,
        clientId: this.selectedClient,
      }).then((result) => {
        this.error = false;
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
        this.importCompleted = true;
        this.$router.back();
        this.openNotification({
          title: 'Import of preferred funds complete.',
        });
        // });
        return result;
      }).catch((error) => {
        this.error = true;
        this.currentStep = 2;
        this.loading = false;
        this.handleImportError(error);
      });
    },
    handleImportError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        goToLogoutRoute(this.$route, this.$router);
      } else {
        // eslint-disable-next-line max-len
        const errorMessage = httpStatus === 400 || httpStatus === 409 ? error.response.data?.data.status?.message : error.message;
        // Custom error messages for failed validation
        this.openNotification({
          title: 'Importing Error',
          message: errorMessage,
          notificationType: 'error',
          showContactInfo: true,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.preferred-funds-container {
  display: flex;
  flex-direction: column;
  .divider {
    border-top: 1px solid $mds-color-neutral-90;
    width: 95%;
  }
}
.preferred-funds-wrapper {
  width: calc(100% - 85px);
}
.preferred-funds-step {
  &__container {
    height: 100%;
    overflow-x: none;
    overflow-y: auto;
    padding: $mds-space-5-x $mds-space-8-x $mds-space-2-x $mds-space-8-x;
  }
  &__title {
    @include mds-level-4-heading();
  }
  .box {
    display: flex;
    justify-content: space-between;
    margin-top: $mds-space-8-x;
  }
  .centered {
    text-align: center;
  }
  p {
    @include mds-body-text-m();
  }
}
.preferred-funds {
  &__file-container {
    background-color: $mds-background-color-light-gray;
    margin: $mds-space-4-x 0;
    padding: $mds-space-8-x;
  }
  &__file-input.mds-form__input {
    background-color: $mds-background-color-light-gray;
    box-shadow: none;
    height: 42px; // Match to design
    margin-top: $mds-space-2-x;
  }
  &__file-input.mds-form__input::-webkit-file-upload-button {
    border: 0;
    border-radius: $mds-space-inset-3-x;
    box-shadow: inset 0 0 0 1px $mds-interactive-color-primary-default;
    background-color: $mds-interactive-color-primary-default;
    color: $mds-color-white;
    padding: $mds-space-1-x $mds-space-2-x;
  }
}
.preferred-funds-result {
  &__header {
    display: inline-block;
  }
  &__title {
    display: inline-block;
    @include mds-level-4-heading();
    font-weight: $mds-font-weight-bold;
    padding: $mds-space-inset-1-x 48px; // Match to design
    margin-bottom: 0;
  }
  &__title.icon-not-ready {
    background: url("/static/assets/not_ready_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-file-excel {
    background: url("/static/assets/file_excel_circle.svg") no-repeat left center;
  }
  &__title.icon-file-add {
    background: url("/static/assets/add_file_circle.svg") no-repeat left center;
  }
  &__title.icon-ready {
    background: url("/static/assets/check_icon_ct.svg") no-repeat left center;
  }
  &__content {
    border-bottom: 1px solid $mds-color-neutral-90;
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    font-weight: $mds-font-weight-thin;
    padding-left: 48px; //Match design
    .preferred-funds-file-type-header {
      font-weight: bold;
    }
  }
}
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  margin-top: $mds-space-4-x;
  width: 100%;

  button {
    margin-left: $mds-space-inset-2-x;
  }
}
</style>
